import type { Season, SeasonProductionModifiers, SeasonState } from './SeasonTypes';
import type { RuntimeWorldTime } from '../../game/runtimeConfig';
import { WeatherSystem } from './WeatherSystem';

const seasonOrder: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];
const defaultSeasonLengthMs = 7 * 24 * 60 * 60 * 1000;

const productionBySeason: Record<Season, SeasonProductionModifiers> = {
  spring: { wood: 1, stone: 1, food: 1.15, gold: 1, fish: 1, movement: 1 },
  summer: { wood: 1, stone: 1, food: 1.3, gold: 1, fish: 1.05, movement: 1.03 },
  autumn: { wood: 1.12, stone: 1, food: 1.2, gold: 1, fish: 0.95, movement: 0.98 },
  winter: { wood: 0.95, stone: 0.92, food: 0.45, gold: 0.95, fish: 0.55, movement: 0.82 },
};

const climateBySeason: Record<Season, Pick<SeasonState, 'temperature' | 'humidity' | 'snowCoverage' | 'iceCoverage'>> = {
  spring: { temperature: 12, humidity: 0.68, snowCoverage: 0, iceCoverage: 0 },
  summer: { temperature: 24, humidity: 0.42, snowCoverage: 0, iceCoverage: 0 },
  autumn: { temperature: 9, humidity: 0.76, snowCoverage: 0, iceCoverage: 0 },
  winter: { temperature: -4, humidity: 0.52, snowCoverage: 0.72, iceCoverage: 0.45 },
};

const missingFinalSeasonTiles: readonly string[] = [];

export class SeasonManager {
  private elapsedMs: number;
  private readonly seasonLengthMs: number;
  private readonly weather = new WeatherSystem();

  constructor(private readonly forcedSeason?: Season, worldTime?: RuntimeWorldTime) {
    this.seasonLengthMs = Math.max(60_000, worldTime?.seasonLengthMs ?? defaultSeasonLengthMs);
    this.elapsedMs = this.forcedSeason
      ? 0
      : Math.max(0, (worldTime?.serverUnixMs ?? Date.now()) - (worldTime?.originUnixMs ?? 0));
  }

  update(deltaMs: number): void {
    if (this.forcedSeason) return;
    this.elapsedMs += Math.max(0, deltaMs);
  }

  getState(): SeasonState {
    const absoluteSeasonIndex = this.forcedSeason ? seasonOrder.indexOf(this.forcedSeason) : Math.floor(this.elapsedMs / this.seasonLengthMs);
    const seasonIndex = this.forcedSeason ? absoluteSeasonIndex : absoluteSeasonIndex % seasonOrder.length;
    const currentSeason = seasonOrder[Math.max(0, seasonIndex)] ?? 'summer';
    const seasonTime = this.forcedSeason ? 0 : this.elapsedMs % this.seasonLengthMs;
    const seasonProgress = seasonTime / this.seasonLengthMs;
    const seasonDay = Math.floor(seasonProgress * 30) + 1;
    const year = this.forcedSeason ? 1 : Math.floor(absoluteSeasonIndex / seasonOrder.length) + 1;

    return {
      currentSeason,
      year,
      seasonDay,
      seasonProgress,
      currentWeather: this.weather.getWeather(currentSeason, seasonDay),
      ...climateBySeason[currentSeason],
      production: productionBySeason[currentSeason],
      missingSeasonTiles: missingFinalSeasonTiles,
    };
  }
}
